import React, { CSSProperties, Fragment, FunctionComponent, ReactNode, useEffect, useState } from 'react';
import { HTMLDivElement } from 'window-or-global';
import Icon, { IconName } from '../../icons/icon/icon-component';
import styles from './radio-component.module.scss';

type RadioProps = {
  name: string;
  /** string | number */
  defaultSelection?: string | number;
  /** Array of {key: string | number, icon, element} objects */
  list?: {
    key: string | number;
    icon?: IconName;
    elt: ReactNode;
  }[];
  form?: 'pill' | 'traditional';
  layout?: 'row' | 'column';
  /** in rem */
  spacing?: number;
  size?: 'default' | 'small';
  radius?: string;
  isDisabled?: boolean;
  /** (key: string | number) => void */
  selectCallback?: (key: string | number) => void;
  class?: string;
  eltClass?: string;
  iconClass?: string;
};

/**
 * Radio component
 *
 * https://www.figma.com/file/jiN4FkdkhDZIWxIkPS6Ru7/design-system?node-id=33%3A1840
 */
export const Radio: FunctionComponent<RadioProps> = ({
  defaultSelection = undefined,
  spacing = 1.4,
  size = 'default',
  isDisabled = false,
  form = 'pill',
  ...props
}) => {
  const styleSpacing = {
    //spacing between radio buttons
    '--radio-spacing': spacing + 'rem',
    // '--radio-direction': props.layout,
  } as React.CSSProperties;

  const [value, setValue] = useState(defaultSelection);

  useEffect(() => {
    setValue(defaultSelection);
  }, [defaultSelection]);

  return (
    <div style={styleSpacing} className={`${styles['radio-component']} ${styles[props.layout ?? 'row']} ${props.class ?? ''}`}>
      {props.list &&
        props.list.map((item) => {
          return (
            <Fragment key={`f_${item.key}`}>
              {/** Pill Radio */}
              {form === 'pill' && (
                <div
                  data-testid={`${item.key.toString()}_${props.name}`}
                  key={item.key}
                  tabIndex={0}
                  onKeyDown={(e) => (!isDisabled ? selectViaEnterKey(e, item.key) : null)}
                  className={`${styles.container} ${item.key === value ? styles['selected-option-container'] : ''}`}
                  id={item.key.toString()}
                  style={{ opacity: isDisabled ? 0.5 : 1 }}
                >
                  <label
                    className={`${styles.label} ${size === 'small' ? styles.small : ''}`}
                    style={{ '--radius': props.radius ?? '0.2rem' } as CSSProperties}
                  >
                    {item.icon ? <Icon name={item.icon} className={`${styles.icon} ${props.iconClass ?? ''}`} /> : null}
                    <input
                      name={props.name}
                      type='radio'
                      onChange={() => (!isDisabled ? select(item.key) : null)}
                      value={item.key}
                      checked={value === item.key}
                    />
                    <span className={props.eltClass ?? ''}>{item.elt}</span>
                  </label>
                </div>
              )}

              {/** Traditional Radio */}
              {form === 'traditional' && (
                <div
                  key={item.key}
                  tabIndex={0}
                  id={item.key.toString()}
                  data-testid={`${item.key.toString()}_${props.name}`}
                  className={styles['traditional-container']}
                  style={{ opacity: isDisabled ? 0.5 : 1 }}
                  onKeyDown={(e) => (!isDisabled ? selectViaEnterKey(e, item.key) : null)}
                  onClick={() => (!isDisabled ? select(item.key) : null)}
                >
                  <input
                    name={props.name}
                    type='radio'
                    className={`${styles['radio-custom']} ${item.key === value && styles['radio-custom-selected']}`}
                    value={item.key}
                    checked={item.key === value}
                    onChange={() => (!isDisabled ? select(item.key) : null)}
                  />
                  <label
                    htmlFor={item.key.toString()}
                    className={`${styles['radio-custom-label']} ${size === 'small' ? styles.small : ''}`}
                  >
                    <div className={item.key === value ? styles['radio-selected'] : styles['radio-unselected']} />
                    {item.icon ? <Icon name={item.icon} className={`${styles.icon} ${props.iconClass ?? ''}`} /> : null}
                    <span className={props.eltClass ?? ''}>{item.elt}</span>
                  </label>
                </div>
              )}
            </Fragment>
          );
        })}
    </div>
  );

  function selectViaEnterKey(e: React.KeyboardEvent<HTMLDivElement>, key: string | number) {
    //if Enter key pressed
    if (e.key === 'Enter') {
      select(key);
    }
  }

  function select(key: string | number) {
    setValue(key);
    props.selectCallback && props.selectCallback(key);
  }
};

export default Radio;
